<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { enablePushNotifications, getPushNotificationStatus, unsubscribeFromPush, getNotificationsApi } from '@/services/pushNotification';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Message from 'primevue/message';
import 'primeicons/primeicons.css';

const isSupported = ref(false);
const isSubscribed = ref(false);
const subscriptionError = ref<string | null>(null);
const supportDetails = ref({ serviceWorker: false, pushManager: false, notifications: false });
const isIOS = ref(/iPhone|iPod|iPad/.test(navigator.platform));
const isIOSStandalone = ref(false);
const isLoading = ref(false);
const testNotificationError = ref<string | null>(null);
const testNotificationSent = ref(false);
const isTesting = ref(false);
const isUnsubscribing = ref(false);
const permissionState = ref<'granted' | 'denied' | 'default'>(typeof Notification !== 'undefined' ? Notification.permission ?? 'default' : 'default');
const userAgent: string = window.navigator.userAgent;

async function updateStatus(): Promise<void> {
  const { isSupported: supported, isSubscribedOnServer, supportDetails: details, isIOSStandalone: standalone } = await getPushNotificationStatus();
  isSupported.value = supported;
  isSubscribed.value = isSubscribedOnServer;
  supportDetails.value = details;
  isIOSStandalone.value = standalone;
  permissionState.value = Notification?.permission ?? 'default';
}

async function enableNotifications(): Promise<void> {
  isLoading.value = true;
  try {
    const { success, error } = await enablePushNotifications();
    if (!success) throw new Error(error);
    await updateStatus();
    subscriptionError.value = null;
  } catch (error: unknown) {
    subscriptionError.value = error instanceof Error ? error.message : 'Unknown error';
  } finally {
    isLoading.value = false;
  }
}

async function unsubscribeFromNotifications(): Promise<void> {
  isUnsubscribing.value = true;
  try {
    await unsubscribeFromPush();
    await updateStatus();
    subscriptionError.value = null;
  } catch (error: unknown) {
    subscriptionError.value = error instanceof Error ? error.message : 'Unknown error';
  } finally {
    isUnsubscribing.value = false;
  }
}

async function sendTestNotification(): Promise<void> {
  if (isIOS.value && !isSubscribed.value && !isIOSStandalone.value) {
    alert('Please add this app to your Home Screen to enable notifications.');
    return;
  }
  isTesting.value = true;
  try {
    await getNotificationsApi().sendTestNotification();
    testNotificationSent.value = true;
    testNotificationError.value = null;
  } catch (error: unknown) {
    testNotificationError.value = error instanceof Error ? error.message : 'Unknown error';
  } finally {
    isTesting.value = false;
  }
}

onMounted(updateStatus);
</script>

<template>
  <div class="p-5 m-auto max-w-[500px]">
    <Card>
      <template #title>
        <h2 class="text-2xl font-bold">Notification Settings</h2>
      </template>
      <template #content>
        <div class="flex flex-col gap-4">
          <Message v-if="isIOS && !isSubscribed && !isIOSStandalone" severity="info" :closable="false">
            Please add this app to your Home Screen to enable notifications (iOS).
          </Message>
          <Message v-if="permissionState === 'denied'" severity="error" :closable="false">
            Notification permission is denied. Please enable it in your browser settings.
          </Message>
          <div v-if="isSubscribed" class="flex items-center gap-2">
            <i class="pi pi-check-circle text-green-500" style="font-size: 1.5rem;"></i>
            <p class="text-color">Notifications are enabled</p>
          </div>
          <Button
            v-if="!isSubscribed"
            label="Enable Notifications"
            icon="pi pi-bell"
            :loading="isLoading"
            class="p-button-raised p-button-success"
            @click="enableNotifications"
          />
          <Button
            v-if="isSubscribed"
            label="Send Test Notification"
            icon="pi pi-send"
            :loading="isTesting"
            class="p-button-raised p-button-info"
            @click="sendTestNotification"
          />
          <Button
            v-if="isSubscribed"
            label="Unsubscribe"
            icon="pi pi-times"
            :loading="isUnsubscribing"
            class="p-button-raised p-button-danger"
            @click="unsubscribeFromNotifications"
          />
          <Message v-if="subscriptionError" severity="error" :closable="false" class="mt-2">
            {{ subscriptionError }}
          </Message>
          <Message v-if="testNotificationSent" severity="success" :closable="false" class="mt-2">
            Test notification sent successfully!
          </Message>
          <Message v-if="testNotificationError" severity="error" :closable="false" class="mt-2">
            {{ testNotificationError }}
          </Message>
        </div>
      </template>
    </Card>
    <Card class="mt-5">
      <template #title>
        <h3 class="text-xl font-bold">Debug Information</h3>
      </template>
      <template #content>
        <div class="flex flex-col gap-2">
          <p><strong>User Agent:</strong> {{ userAgent }}</p>
          <p><strong>Is iOS:</strong> {{ isIOS ? 'Yes' : 'No' }}</p>
          <p><strong>Is iOS Standalone:</strong> {{ isIOSStandalone ? 'Yes' : 'No' }}</p>
          <p><strong>Notifications Supported:</strong> {{ isSupported ? 'Yes' : 'No' }}</p>
          <p><strong>Permission State:</strong> {{ permissionState }}</p>
          <p><strong>Subscribed:</strong> {{ isSubscribed ? 'Yes' : 'No' }}</p>
        </div>
      </template>
    </Card>
  </div>
</template>